import React from "react";
import { Award, Trophy, Crown, Star, Flame, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { ExhibitionBadge } from "@/hooks/useExhibitionEvents";
import { format } from "date-fns";

interface ExhibitionBadgesProps {
  badges: ExhibitionBadge[];
  compact?: boolean;
}

const BADGE_CONFIG: Record<string, {
  label: string;
  icon: React.ReactNode;
  gradient: string;
  description: string;
}> = {
  most_requested_booth: {
    label: "Most Requested Booth",
    icon: <Flame className="w-5 h-5" />,
    gradient: "from-orange-500 via-amber-500 to-yellow-500",
    description: "Your booth was the most requested during the event",
  },
  top_host: {
    label: "Top Host",
    icon: <Crown className="w-5 h-5" />,
    gradient: "from-purple-500 via-violet-500 to-indigo-500",
    description: "You hosted the most engaging booth",
  },
  popular_exhibitor: {
    label: "Popular Exhibitor",
    icon: <Star className="w-5 h-5" />,
    gradient: "from-pink-500 via-rose-500 to-red-500",
    description: "You received the most pops during the event",
  },
};

const ExhibitionBadges: React.FC<ExhibitionBadgesProps> = ({ badges, compact }) => {
  if (badges.length === 0) {
    return (
      <div className="mx-4 mt-4 p-6 rounded-2xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 text-center">
        <Award className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <h3 className="font-semibold text-foreground mb-1">No Badges Yet</h3>
        <p className="text-sm text-muted-foreground">
          Participate in Love Exhibition events to earn badges!
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4 py-2">
        {badges.slice(0, 5).map((badge) => {
          const config = BADGE_CONFIG[badge.badge_type];
          if (!config) return null;

          return (
            <div
              key={badge.id}
              className={cn(
                "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg bg-gradient-to-br",
                config.gradient
              )}
              title={config.label}
            >
              {config.icon}
            </div>
          );
        })}
        {badges.length > 5 && (
          <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-muted text-muted-foreground font-bold">
            +{badges.length - 5}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-4 mt-4 rounded-2xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/40 dark:border-gray-700/40">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-bold text-foreground">Your Badges</h3>
          <span className="ml-auto text-sm text-muted-foreground">
            {badges.length} earned
          </span>
        </div>
      </div>

      {/* Badges grid */}
      <div className="p-4 grid grid-cols-2 gap-3">
        {badges.map((badge) => {
          const config = BADGE_CONFIG[badge.badge_type];
          if (!config) return null;

          return (
            <div
              key={badge.id}
              className="relative rounded-xl overflow-hidden bg-gradient-to-br from-white/80 to-white/40 dark:from-gray-800/80 dark:to-gray-800/40 border border-white/60 dark:border-gray-700/60 p-3"
            >
              {/* Badge icon */}
              <div
                className={cn(
                  "w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg mb-3 bg-gradient-to-br",
                  config.gradient
                )}
              >
                {config.icon}
                {badge.rank && badge.rank <= 3 && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center text-xs font-bold text-amber-900 shadow">
                    #{badge.rank}
                  </div>
                )}
              </div>

              {/* Badge info */}
              <h4 className="font-semibold text-foreground text-sm mb-1">
                {config.label}
              </h4>
              
              {badge.booth_name && (
                <p className="text-xs text-muted-foreground truncate mb-1">
                  {badge.booth_name}
                </p>
              )}

              {badge.city_name && badge.country_name && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">
                    {badge.city_name}, {badge.country_name}
                  </span>
                </div>
              )}

              <p className="text-[10px] text-muted-foreground/70 mt-2">
                {format(new Date(badge.earned_at), "MMM d, yyyy")}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExhibitionBadges;
